<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import { productBus } from '@/event/product/productBus'
import { relatedGroupBus } from '@/event/product/relatedGroupeBus'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { onHover } from '@/happyTheme/utils/onHover'
import { toggleSelectedModalProductHappy } from '@/happyTheme/utils/product/toggleSelectedModalProductHappy'
import { excludeRouteDropdownCategoryArray } from '@/storage/navbar/excludeRoute'
import { getOptionName } from '@/utils/product/getOptionName'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SelectOptionProductHappy from './SelectOptionProductHappy.vue'
import InputQuantityProductHappy from '@/happyTheme/components/product/InputQuantityProductHappy.vue'
import { type SelectedOptionHappy } from '@/happyTheme/interfaces/product/selectedOptionInterface'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { openStatesHappy } from '@/happyTheme/event/product/openStatesHappy'
import { getEmptySelectedValuesHappy } from '@/happyTheme/utils/product/modal/getEmptySelectedValuesHappy'
import {
    messageErrorSelectOptionHappy,
    messageErrorSelectOptionHappyEmpty
} from '@/happyTheme/event/product/messageErrorSelectOptionHappy'
import { visibleOptionsHappy } from '@/happyTheme/event/product/visibleOptionHappy'
import MessageErrorOptionHappy from '@/happyTheme/components/product/MessageErrorOptionHappy.vue'
import { selectedPriceProductHappy } from '@/happyTheme/utils/product/modal/selectedPriceProductHappy'
import { addToCartHappy } from '@/happyTheme/utils/product/modal/addToCartHappy'
import { showBannerMessageSpecial } from '@/happyTheme/event/messageSpecial/showBannerMessageSpecial'
import { showHeader } from '@/happyTheme/event/layout/showHeader'
import { TimeOutClass } from '@/happyTheme/services/timeout/TimeOutClass'
import { productAssociated } from '@/event/product/productAssociated'
import ProductAssociated from '@/components/product/ProductAssociated.vue'
import ButtonBasketProductHappy from '@/happyTheme/components/product/ButtonBasketProductHappy.vue'
import { showAlertValidateProductHappy } from '@/happyTheme/event/product/showAlertValidateProductHappy'
import { showProductAssociated } from '@/happyTheme/event/product/showProductAssociated'
import { groupByNameEnhancedHappy } from '@/happyTheme/utils/product/groupeByNameEnhancedHappy'

const route = useRoute()
const optionRefs = ref<Record<number, HTMLElement>>({})
const resizeObservers = ref<Record<number, ResizeObserver>>({})

const heightBottomModal = ref(64)

const {
    isActive: isHovered1,
    onPointerEnter: onEnter1,
    onPointerLeave: onLeave1,
    onClick: onClick1,
    onUnclick: onUnclick1
} = onHover()

const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
        optionRefs.value[index] = el

        if (!resizeObservers.value[index]) {
            resizeObservers.value[index] = new ResizeObserver(() => {
                updateHeightForIdx(index)
            })
        }
        resizeObservers.value[index].observe(el)
    } else {
        if (resizeObservers.value[index]) {
            resizeObservers.value[index].disconnect()
            delete resizeObservers.value[index]
        }
        delete optionRefs.value[index]
    }
}

const handleScroll = () => {
    const modalBody = document.getElementById('modal-body-product-selected-happy')
    if (!modalBody) return

    const viewportHeight = window.innerHeight
    const viewportCenter = viewportHeight / 2

    if (
        !visibleOptionsHappy.value ||
        visibleOptionsHappy.value.length !== productBus.value?.options.length
    ) {
        visibleOptionsHappy.value = new Array(productBus.value?.options.length || 0).fill(
            false
        )
    } else {
        visibleOptionsHappy.value = visibleOptionsHappy.value.map(() => false)
    }

    let closestElement = null
    let closestDistance = Infinity

    const openedIndices: number[] = []

    openStatesHappy.value.forEach((isOpen, idx) => {
        if (!isOpen) return

        openedIndices.push(idx)

        const optionElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${idx}"]`
        ) as HTMLElement | null
        if (!optionElement) return

        const rect = optionElement.getBoundingClientRect()
        const elementTop = rect.top
        const elementHeight = rect.height
        const elementBottom = rect.bottom
        const elementCenter = elementTop + elementHeight / 2

        const isVisible = elementBottom > 0 && elementTop < viewportHeight
        if (!isVisible) return

        const distanceFromCenter = Math.abs(viewportCenter - elementCenter)

        if (distanceFromCenter < closestDistance) {
            closestDistance = distanceFromCenter
            closestElement = idx
        }
    })

    if (openedIndices.length) {
        const firstIdx = openedIndices[0]
        const lastIdx = openedIndices[openedIndices.length - 1]

        const firstElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${firstIdx}"]`
        )
        const lastElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${lastIdx}"]`
        )

        if (firstElement && firstElement.getBoundingClientRect().top > viewportHeight) {
            closestElement = firstIdx
        } else if (lastElement && lastElement.getBoundingClientRect().bottom < 0) {
            closestElement = lastIdx
        }
    }

    if (closestElement !== null) {
        visibleOptionsHappy.value[closestElement] = true
    }
}

function selectedOption(option: SelectedOptionHappy) {
    selectOptionsListHappy.value[option.option_id] = option
}

function getTotalSelectedQuantityRadio(optionId: number): number {
    if (selectOptionsListHappy.value[optionId]) {
        const radioOptionId =
            selectOptionsListHappy.value[optionId].selected_values.radio_option
                .option_value_id

        return radioOptionId ? 1 : 0
    }

    return 0
}

function getTotalSelectedQuantitySelect(optionId: number) {
    let result = {
        freeQuantity: 0,
        paidQuantity: 0,
        freeActiveQuantity: 0,
        paidActiveQuantity: 0
    }

    if (!selectOptionsListHappy.value[optionId]) {
        return result
    }

    const option = productBus.value?.options.find((product) => {
        return product.option_id === optionId
    })

    if (option?.type === 'select' || option?.type === 'checkbox') {
        const countResetFree = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_free.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        const countResetPaid = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_paid.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        result.freeActiveQuantity = countResetFree
        result.paidActiveQuantity = countResetPaid
    }

    const resetOption =
        option?.values.filter((o) => o.reset == 1).map((o) => o.option_value_id) || []

    if (
        (option?.free_option_count === 0 && option.max_option_count === 0) ||
        (option?.free_option_count !== 0 && option?.max_option_count === 0)
    ) {
        result.freeQuantity = option.free_option_count
        result.paidQuantity = option.values.length - resetOption?.length
        return result
    }

    if (
        option?.free_option_count !== 0 &&
        option?.max_option_count &&
        option?.max_option_count !== 0 &&
        option?.free_option_count
    ) {
        result.freeQuantity = 0
        result.paidQuantity = option?.max_option_count - option?.free_option_count || 0
        return result
    }

    return result
}

function updateHeightForIdx(idx: number) {
    const openedIndices = openStatesHappy.value
        .map((isOpen, i) => (isOpen ? i : -1))
        .filter((i) => i >= 0)

    let maxHeight = 64

    openedIndices.forEach((openedIdx) => {
        const openedEl = optionRefs.value[openedIdx]
        if (openedEl) {
            const h = openedEl.getBoundingClientRect().height || 0
            maxHeight = Math.max(maxHeight, Math.round(h + 72))
        }
    })

    window.requestAnimationFrame(() => {
        heightBottomModal.value = maxHeight
    })
}

async function toggleShowDropdownOption(index: number) {
    openStatesHappy.value[index] = !openStatesHappy.value[index]

    visibleOptionsHappy.value[index] = openStatesHappy.value[index]

    await nextTick()
    updateHeightForIdx(index)
}

const getEnhancedGroupedOptions = (optionId: number) => {
    if (!selectOptionsListHappy.value[optionId]) return []

    const option = productBus.value?.options.find((opt) => opt.option_id === optionId)
    if (!option) return []

    const allOptions = []

    // Radio option
    if (
        option.type === 'radio' &&
        selectOptionsListHappy.value[optionId].selected_values.radio_option
            .option_value_id
    ) {
        if (
            Number(
                selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_price
            ) > 0
        ) {
            allOptions.push({
                id: `radio-${optionId}`,
                name: selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_name,
                type: 'radio',
                count: 1,
                price:
                    selectOptionsListHappy.value[optionId].selected_values.radio_option
                        .option_value_price || 0
            })
        } else {
            allOptions.push({
                id: `radio-${optionId}`,
                name: selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_name,
                type: 'radio',
                count: 1,
                price:
                    selectOptionsListHappy.value[optionId].selected_values.radio_option
                        .option_value_price || 0
            })
        }
    }

    // Free options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_free.length > 0
    ) {
        const freeOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_free
        const groupedFree = groupByNameEnhancedHappy(freeOptions, 'free', optionId)
        allOptions.push(...groupedFree)
    }

    // Paid options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_paid.length > 0
    ) {
        const paidOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_paid
        const groupedPaid = groupByNameEnhancedHappy(paidOptions, 'paid', optionId)
        allOptions.push(...groupedPaid)
    }

    return allOptions
}

watch(
    () => productBus.value?.options,
    (opts) => {
        selectOptionsListHappy.value = {}
        if (!opts) return
        opts.forEach((option) => {
            selectOptionsListHappy.value[option.option_id] =
                getEmptySelectedValuesHappy(option)
        })

        openStatesHappy.value = new Array(opts.length).fill(false)
        visibleOptionsHappy.value = new Array(opts.length).fill(false)

        messageErrorSelectOptionHappy.value = new Array(opts.length).fill(
            messageErrorSelectOptionHappyEmpty
        )

        if (opts.length > 0) {
            openStatesHappy.value[0] = true
            visibleOptionsHappy.value[0] = true
        }
    },
    { immediate: true }
)

watch(
    () => showSelectedModalProductHappy.value,
    (isVisible) => {
        if (!isVisible) {
            const html = document.documentElement
            if (html.classList.contains('modal-open')) {
                html.classList.remove('modal-open')
            }
        }
    }
)

watch(
    () => route.name,
    (routeName) => {
        if (routeName) {
            const routeNameString = String(routeName)
            if (!excludeRouteDropdownCategoryArray.includes(routeNameString)) {
                showSelectedModalProductHappy.value = false
            }
        }
    },
    {
        deep: true
    }
)

watch(
    openStatesHappy,
    async () => {
        await nextTick()

        const opened = openStatesHappy.value
            .map((isOpen, idx) => (isOpen ? idx : -1))
            .filter((idx) => idx >= 0)

        if (opened.length === 0) {
            heightBottomModal.value = 64
            visibleOptionsHappy.value = visibleOptionsHappy.value.map(() => false)
            return
        }
    },
    { deep: true, immediate: true }
)

onMounted(() => {
    showAlertValidateProductHappy.value = false
    showProductAssociated.value = false
    openStatesHappy.value = openStatesHappy.value.map(() => false)
    handleScroll()
    showHeader.value = true

    const idTimeOut = setTimeout(() => {
        showSelectedModalProductHappy.value = true

        nextTick(() => {
            const modalBody = document.getElementById('modal-body-product-selected-happy')
            if (modalBody) {
                modalBody.addEventListener('scroll', handleScroll)
            }
        })
    }, 200)
    const timeOutClass = TimeOutClass.getInstance()
    timeOutClass.setTimeoutWithKey('showModalProductHappy', idTimeOut)

    if (!productBus.value) return
    productBus.value.options.forEach((option) => {
        if (!selectOptionsListHappy.value[option.option_id]) {
            selectOptionsListHappy.value[option.option_id] =
                getEmptySelectedValuesHappy(option)
        }
    })
    visibleOptionsHappy.value = new Array(productBus.value.options.length).fill(false)

    if (productBus.value.options.length > 0) {
        openStatesHappy.value[0] = true
        visibleOptionsHappy.value[0] = true
    }
})

onBeforeUnmount(() => {
    resizeObservers.value = {}
    window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
    <section
        class="section-modal-product-selected-happy"
        v-if="productBus && openStatesHappy.length === productBus.options.length"
    >
        <!-- <Transition
            name="slide-overlay-product"
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-300 ease-out"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
        > -->
        <section
            @click.stop="toggleSelectedModalProductHappy()"
            v-if="showSelectedModalProductHappy"
            class="overlay-section-product-happy"
        ></section>
        <!-- </Transition> -->

        <!-- <Transition
            name="slide-modal-product"
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-300 ease-out"
            leave-from-class="translate-x-0"
            leave-to-class="translate-x-full"
        > -->
        <section
            class="section-selected-product-happy"
            :class="{
                'padding-with-banner': showBannerMessageSpecial,
                'padding-without-banner': !showBannerMessageSpecial
            }"
            v-if="showSelectedModalProductHappy"
        >
            <div class="div-button-close-product-selected-happy">
                <button
                    class="close-modal-product-selected-happy"
                    @click="toggleSelectedModalProductHappy"
                >
                    <svg
                        class="svg-cross-product-selected-happy"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M16 8L8 16M8.00001 8L16 16"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <section
                class="modal-body-product-selected-happy"
                id="modal-body-product-selected-happy"
                v-if="!showProductAssociated"
            >
                <section class="section-product-selected-happy">
                    <div class="div-product-selected-happy">
                        <img
                            loading="lazy"
                            class="img-product-selected-happy"
                            :src="`${URL_IMAGE}${productBus.image}`"
                            alt=""
                        />
                    </div>

                    <div class="div-product-information-selected-happy">
                        <h3 class="h3-product-selected-happy">
                            {{ getProductName(productBus) }}
                        </h3>

                        <p
                            v-if="
                                hasDescriptionForLanguage(
                                    productBus.description,
                                    languageIdBus
                                )
                            "
                        >
                            {{ getProductDescription(productBus) }}
                        </p>
                    </div>
                </section>

                <section
                    class="section-product-option-selected-happy"
                    v-if="productBus.options.length > 0"
                >
                    <div class="div-product-option-information-happy">
                        <h4 class="h4-product-option-selected-happy">Options</h4>

                        <p class="text-obligation-option-selected-happy">
                            <span class="text-sm">*</span>Obligatoire
                        </p>
                    </div>
                    <section class="div-form-product-happy">
                        <div
                            v-for="(option, idx) in productBus.options"
                            :key="option.option_id"
                            :id="String(option.option_id)"
                            class="w-full"
                            :class="{
                                hidden: relatedGroupBus.includes(option.option_id)
                            }"
                        >
                            <div
                                class="div-v-for-option-product-happy"
                                :data-index="idx"
                                :class="{
                                    shake:
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error',
                                    'border-red-eronor':
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error'
                                }"
                            >
                                <div
                                    v-if="!relatedGroupBus.includes(option.option_id)"
                                    :class="{
                                        'border-title-option-happy': openStatesHappy[idx]
                                    }"
                                    @click.stop="toggleShowDropdownOption(idx)"
                                >
                                    <div class="form-option-title-product-happy">
                                        <h4
                                            :id="getOptionName(option, languageIdBus)"
                                            v-if="
                                                option.required &&
                                                option.type === 'select'
                                            "
                                            class="h4-option-happy"
                                        >
                                            <p class="">
                                                {{ getOptionName(option, languageIdBus)
                                                }}<span
                                                    v-if="option.required"
                                                    class="choice"
                                                    >*</span
                                                >
                                            </p>
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' &&
                                                !option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' && option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus)
                                            }}<span class="choice">*</span>
                                        </h4>

                                        <h4
                                            v-else
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <svg
                                            class="svg-arrow-happy-option-product-happy"
                                            :class="{
                                                'rotate-180': openStatesHappy[idx],
                                                'pb-0.5': !openStatesHappy[idx]
                                            }"
                                            viewBox="0 0 16 10"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M15.6994 0.852272C15.2985 0.45142 14.6486 0.45142 14.2478 0.852272L8.13076 6.96921L2.01369 0.852272C1.61284 0.45142 0.963042 0.45142 0.56219 0.852272C0.161338 1.25312 0.161338 1.90292 0.56219 2.30377L7.40501 9.14663C7.59743 9.33912 7.85855 9.44727 8.13076 9.44727C8.40297 9.44727 8.66409 9.33912 8.85651 9.14663L15.6994 2.30377C16.1002 1.90292 16.1002 1.25312 15.6994 0.852272Z"
                                            />
                                        </svg>
                                    </div>

                                    <div
                                        v-if="
                                            selectOptionsListHappy[option.option_id] &&
                                            (selectOptionsListHappy[option.option_id]
                                                .selected_values.radio_option
                                                .option_value_id !== 0 ||
                                                selectOptionsListHappy[option.option_id]
                                                    .selected_values.checkbox_option
                                                    .option_value_free.length > 0 ||
                                                selectOptionsListHappy[option.option_id]
                                                    .selected_values.checkbox_option
                                                    .option_value_paid.length > 0)
                                        "
                                        class="option-selected-happy"
                                    >
                                        <TransitionGroup
                                            name="option-cascade"
                                            tag="div"
                                            enter-active-class="transition-all duration-400 ease-out"
                                            enter-from-class="opacity-0 transform translate-x-4 scale-90"
                                            enter-to-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-active-class="transition-all duration-300 ease-in"
                                            leave-from-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-to-class="opacity-0 transform -translate-x-4 scale-90"
                                            move-class="transition-none"
                                        >
                                            <div
                                                v-for="groupedOption in getEnhancedGroupedOptions(
                                                    option.option_id
                                                )"
                                                :key="groupedOption.id"
                                                class="grouped-option-item"
                                                :class="{
                                                    'option-paid':
                                                        groupedOption.type === 'paid',
                                                    'option-free':
                                                        groupedOption.type === 'free',
                                                    'option-radio':
                                                        groupedOption.type === 'radio'
                                                }"
                                            >
                                                <span class="option-name">
                                                    - {{ groupedOption.name }}
                                                </span>

                                                <!-- ✅ Badge pour la quantité -->
                                                <span
                                                    v-if="groupedOption.count > 1"
                                                    class="quantity-badge"
                                                >
                                                    x{{ groupedOption.count }}
                                                </span>

                                                <!-- ✅ Prix total si payant -->
                                                <span
                                                    v-if="
                                                        groupedOption.type === 'paid' &&
                                                        'totalPrice' in groupedOption &&
                                                        groupedOption.totalPrice > 0
                                                    "
                                                    class="price-info-happy"
                                                >
                                                    (+{{
                                                        groupedOption.totalPrice.toFixed(
                                                            2
                                                        )
                                                    }}€)
                                                </span>

                                                <span
                                                    v-if="
                                                        groupedOption.type === 'radio' &&
                                                        Number(groupedOption.price) > 0
                                                    "
                                                    class="price-info-happy"
                                                >
                                                    (+{{
                                                        Number(
                                                            groupedOption.price
                                                        ).toFixed(2)
                                                    }}€)
                                                </span>
                                            </div>
                                        </TransitionGroup>
                                    </div>

                                    <MessageErrorOptionHappy
                                        v-if="
                                            messageErrorSelectOptionHappy[idx].message &&
                                            messageErrorSelectOptionHappy[idx].type
                                        "
                                        :message="
                                            messageErrorSelectOptionHappy[idx].message
                                        "
                                        :type="messageErrorSelectOptionHappy[idx].type"
                                    />
                                </div>

                                <div
                                    class="dropdown-container"
                                    :class="{
                                        'dropdown-open': openStatesHappy[idx],
                                        'dropdown-closed': !openStatesHappy[idx]
                                    }"
                                >
                                    <SelectOptionProductHappy
                                        v-if="openStatesHappy[idx]"
                                        :options="option"
                                        :index="idx"
                                        :name_option="
                                            getOptionName(option, languageIdBus)
                                        "
                                        @select-option="
                                            (opt: SelectedOptionHappy) =>
                                                selectedOption(opt)
                                        "
                                    />
                                </div>
                            </div>

                            <div
                                v-if="openStatesHappy[idx]"
                                :ref="(el) => setOptionRef(el as HTMLElement, idx)"
                                class="div-option-selected-happy"
                                :data-index="idx"
                                :class="{
                                    'fixed-bottom ':
                                        openStatesHappy[idx] && visibleOptionsHappy[idx],
                                    'fixed -bottom-full opacity-0':
                                        openStatesHappy[idx] && !visibleOptionsHappy[idx]
                                }"
                            >
                                <div>
                                    <p
                                        class="font-semibold"
                                        v-if="visibleOptionsHappy[idx]"
                                    >
                                        {{ getOptionName(option, languageIdBus) }}
                                    </p>
                                    <span
                                        v-if="
                                            option.values.find(
                                                (e) => e.price_status === 'special'
                                            )
                                        "
                                        class="text-xs italic font-bold text-border-red-happy-light"
                                        >certaines options sont payantes
                                    </span>
                                    <span
                                        class="span-choice-happy-required"
                                        v-if="option.type === 'radio' && option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="option.type === 'radio' && !option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        v-if="option.type === 'select' && option.required"
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="
                                            option.type === 'select' && !option.required
                                        "
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selecti-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div :style="{ minHeight: `${heightBottomModal}px` }"></div>
                </section>
                <div
                    class="modal-footer-product-happy"
                    :class="{
                        'fixed bottom-0 items-center': productBus.options.length > 0,
                        'flex-grow h-screen items-start': productBus.options.length === 0
                    }"
                >
                    <InputQuantityProductHappy />
                    <div class="relative flex items-center justify-center">
                        <button
                            class="button-add-to-cart-happy"
                            @click="addToCartHappy()"
                        >
                            Ajouter {{ selectedPriceProductHappy(productBus.price) }} €
                        </button>
                        <ButtonBasketProductHappy v-if="showAlertValidateProductHappy" />
                    </div>
                </div>
            </section>

            <section
                v-if="productAssociated && showProductAssociated"
                class="section-product-associated-happy"
            >
                <ProductAssociated />
                <div :style="{ minHeight: `${heightBottomModal}px` }"></div>
            </section>
        </section>
        <!-- </Transition> -->
    </section>
</template>

<style scoped>
.section-modal-product-selected-happy {
    @apply z-20 fixed top-0 w-screen  overflow-y-auto flex justify-end;
    height: 100%;
}

.overlay-section-product-happy {
    @apply fixed top-0 w-screen h-full opacity-70;
    @apply happy-light:bg-text-dark-happy-light;
    @apply happy-dark:bg-bg-happy-light;
    @apply happy-urban-dark-red:bg-bg-happy-light;
    @apply happy-urban-light-red:bg-urban-dark;
    @apply happy-orange-light:bg-urban-dark;
    @apply happy-orange-dark:bg-bg-happy-light;
    @apply happy-fresh-mint:bg-urban-dark;
    @apply happy-bee-light:bg-urban-dark;
    @apply happy-bee-dark:bg-bg-happy-light;
    @apply happy-earth-stone:bg-urban-dark;
    @apply happy-scorched-earth:bg-urban-dark;
    @apply happy-sand-sky:bg-urban-dark;
    @apply happy-gray-sand:bg-urban-dark;
    @apply happy-purple-gradient:bg-urban-dark;
}

.section-selected-product-happy {
    @apply relative w-full min-h-screen;
    @apply flex flex-col z-10;
    @apply happy-light:bg-border-gray-happy-light;
    @apply happy-dark:bg-border-gray-happy-light;
    @apply happy-urban-dark-red:bg-urban-dark;
    @apply happy-urban-light-red:bg-bg-happy-light;
    @apply happy-orange-light:bg-bg-happy-light;
    @apply happy-orange-dark:bg-urban-dark;
    @apply happy-fresh-mint:bg-bg-happy-light;
    @apply happy-bee-light:bg-bee-yellow;
    @apply happy-bee-dark:bg-urban-dark;
    @apply happy-earth-stone:bg-bg-earth;
    @apply happy-scorched-earth:bg-bg-scorched-earth;
    @apply happy-sand-sky:bg-bg-sand;
    @apply happy-gray-sand:bg-bg-gray-sand;
    @apply happy-purple-gradient:bg-gradient-to-t happy-purple-gradient:to-bg-purple-dark happy-purple-gradient:from-bg-purple-light;
}

.padding-without-banner {
    padding-top: var(--navbar-height, 5rem);
}

.padding-with-banner {
    padding-top: var(--navbar-banner-total-height, 5rem);
}

.div-button-close-product-selected-happy {
    @apply fixed flex p-1 shrink-0 z-10 mt-2 ml-2 rounded-xl shadow-sm shadow-bg-happy-light;
    @apply happy-light:bg-text-dark-happy-light happy-light:shadow-border-gray-happy-light;
    @apply happy-dark:bg-bg-happy-light;
    @apply happy-urban-dark-red:bg-urban-red;
    @apply happy-urban-light-red:bg-urban-dark happy-urban-light-red:shadow-border-gray-happy-light;
    @apply happy-orange-light:bg-bg-orange happy-orange-light:shadow-urban-dark;
    @apply happy-orange-dark:bg-bg-orange;
    @apply happy-fresh-mint:bg-bg-mint happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:bg-urban-dark happy-bee-light:shadow-gray-sand-gray-light;
    @apply happy-bee-dark:bg-bg-happy-light happy-bee-dark:shadow-urban-dark;
    @apply happy-earth-stone:bg-earth-card happy-earth-stone:shadow-urban-dark;
    @apply happy-scorched-earth:bg-scorched-earth-yellow happy-scorched-earth:shadow-urban-dark;
    @apply happy-sand-sky:bg-sand-blue happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:bg-border-gray-happy-light happy-gray-sand:shadow-urban-dark;
}

.svg-cross-product-selected-happy {
    @apply w-10;
    @apply happy-light:stroke-bg-happy-light;
    @apply happy-dark:stroke-text-dark-happy-light;
    @apply happy-urban-dark-red:stroke-bg-happy-light;
    @apply happy-urban-light-red:stroke-bg-happy-light;
    @apply happy-orange-light:stroke-urban-dark;
    @apply happy-orange-dark:stroke-urban-dark;
    @apply happy-fresh-mint:stroke-urban-dark;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-urban-dark;
    @apply happy-earth-stone:stroke-urban-dark;
    @apply happy-scorched-earth:stroke-urban-dark;
    @apply happy-sand-sky:stroke-urban-dark;
    @apply happy-gray-sand:stroke-urban-dark;
}

.div-button-close-product-selected-happy:active,
.div-button-close-product-selected-happy:focus {
    @apply happy-light:bg-border-gray-happy-light;
    @apply happy-dark:bg-border-gray-happy-light;
    @apply happy-urban-dark-red:bg-urban-red-dark;
    @apply happy-urban-light-red:bg-urban-red-dark;
    @apply happy-orange-light:bg-bg-orange-dark;
    @apply happy-orange-dark:bg-bg-orange-dark;
    @apply happy-fresh-mint:bg-bg-mint-dark;
    @apply happy-bee-light:bg-bee-yellow-dark;
    @apply happy-bee-dark:bg-bee-yellow-dark;
    @apply happy-earth-stone:bg-earth-card-dark;
    @apply happy-scorched-earth:bg-scorched-earth-red;
    @apply happy-sand-sky:bg-sand-blue-dark;
    @apply happy-gray-sand:bg-gray-sand-gray-light;
}

.div-button-close-product-selected-happy:active .svg-cross-product-selected-happy,
.div-button-close-product-selected-happy:focus .svg-cross-product-selected-happy {
    @apply happy-light:stroke-text-dark-happy-light;
    @apply happy-dark:stroke-text-dark-happy-light;
    @apply happy-orange-light:stroke-bg-happy-light;
    @apply happy-orange-dark:stroke-bg-happy-light;
    @apply happy-fresh-mint:stroke-bg-happy-light;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-bg-happy-light;
    @apply happy-earth-stone:stroke-bg-happy-light;
    @apply happy-scorched-earth:stroke-bg-happy-light;
    @apply happy-sand-sky:stroke-bg-happy-light;
    @apply happy-gray-sand:stroke-text-dark-happy-light;
}

.modal-body-product-selected-happy {
    @apply flex flex-col overflow-y-auto;
}

.section-product-selected-happy {
    @apply max-w-full flex flex-col p-2 happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
    min-height: fit-content;
}

.div-product-selected-happy {
    @apply w-full flex flex-col items-center gap-2 mb-2 happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
}

.img-product-selected-happy {
    @apply max-w-80;
}

.div-product-information-selected-happy {
    @apply w-full flex flex-col gap-2;
}

.h3-product-selected-happy {
    @apply font-bold text-3xl;
}

.div-product-option-selected-happy {
    @apply w-full;
}
.section-product-option-selected-happy {
    @apply w-full h-full flex flex-col px-2 pt-2;
    @apply happy-light:bg-border-gray-happy-light;
    @apply happy-dark:bg-border-gray-happy-light;
    @apply happy-urban-dark-red:bg-urban-dark happy-urban-dark-red:border-t happy-urban-dark-red:border-bg-happy-light;
    @apply happy-urban-light-red:bg-bg-happy-light happy-urban-light-red:border-t happy-urban-light-red:border-text-dark-happy-light;
    @apply happy-orange-light:bg-bg-happy-light happy-orange-light:border-t happy-orange-light:border-urban-dark;
    @apply happy-orange-dark:bg-urban-dark happy-orange-dark:border-t happy-orange-dark:border-bg-orange;
    @apply happy-fresh-mint:bg-bg-happy-light happy-fresh-mint:border-t happy-fresh-mint:border-bg-mint;
    @apply happy-bee-light:bg-bee-yellow happy-bee-light:border-t happy-bee-light:border-urban-dark;
    @apply happy-bee-dark:bg-urban-dark happy-bee-dark:border-t happy-bee-dark:border-bee-yellow;
    @apply happy-earth-stone:bg-bg-earth happy-earth-stone:border-t happy-earth-stone:border-earth-card;
    @apply happy-scorched-earth:bg-bg-scorched-earth happy-scorched-earth:border-t happy-scorched-earth:border-scorched-earth-yellow;
    @apply happy-sand-sky:bg-bg-sand happy-sand-sky:border-t happy-sand-sky:border-urban-dark;
    @apply happy-gray-sand:bg-bg-gray-sand happy-gray-sand:border-t happy-gray-sand:border-urban-dark;
}

.div-product-option-information-happy {
    @apply w-full flex justify-between items-center;
}

.h4-product-option-selected-happy {
    @apply font-semibold text-lg pb-2 text-text-dark-happy-light;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light;
    @apply happy-urban-light-red:text-urban-dark;
    @apply happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:text-bg-happy-light;
    @apply happy-fresh-mint:text-urban-dark;
    @apply happy-bee-light:text-urban-dark;
    @apply happy-bee-dark:text-bg-happy-light;
    @apply happy-earth-stone:text-urban-dark;
    @apply happy-scorched-earth:text-urban-dark;
    @apply happy-sand-sky:text-urban-dark;
    @apply happy-gray-sand:text-urban-dark;
}

.text-obligation-option-selected-happy {
    @apply text-xs  font-semibold;
    @apply happy-light:text-border-red-happy-light;
    @apply happy-dark:text-border-red-happy-light;
    @apply happy-urban-dark-red:text-urban-red;
    @apply happy-urban-light-red:text-urban-red;
    @apply happy-orange-light:text-urban-red;
    @apply happy-orange-dark:text-urban-red;
    @apply happy-fresh-mint:text-urban-red-dark;
    @apply happy-bee-light:text-urban-red;
    @apply happy-bee-dark:text-urban-red;
    @apply happy-earth-stone:text-urban-red-dark;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-urban-red;
    @apply happy-gray-sand:text-urban-red;
}

.div-form-product-happy {
    @apply flex flex-col gap-3;
}

.div-v-for-option-product-happy {
    @apply rounded-xl border-2 w-full;
    @apply happy-light:bg-bg-happy-light happy-light:border-bg-happy-light;
    @apply happy-dark:bg-text-dark-happy-light happy-dark:border-text-dark-happy-light;
    @apply happy-urban-dark-red:bg-urban-red happy-urban-dark-red:border-bg-happy-light happy-urban-dark-red:text-bg-happy-light;
    @apply happy-urban-light-red:bg-urban-red happy-urban-light-red:border-text-dark-happy-light happy-urban-light-red:text-bg-happy-light;
    @apply happy-orange-light:bg-bg-orange happy-orange-light:border-urban-dark happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:bg-bg-orange happy-orange-dark:border-bg-happy-light happy-orange-dark:text-urban-dark;
    @apply happy-fresh-mint:bg-bg-mint happy-fresh-mint:border-urban-dark happy-fresh-mint:text-urban-dark;
    @apply happy-bee-light:bg-bg-happy-light happy-bee-light:border-urban-dark happy-bee-light:text-urban-dark;
    @apply happy-bee-dark:bg-urban-dark happy-bee-dark:border-bee-yellow happy-bee-dark:text-bg-happy-light;
    @apply happy-earth-stone:bg-earth-card happy-earth-stone:border-urban-dark happy-earth-stone:text-urban-dark;
    @apply happy-scorched-earth:bg-scorched-earth-yellow happy-scorched-earth:border-urban-dark happy-scorched-earth:text-urban-dark;
    @apply happy-sand-sky:bg-sand-blue happy-sand-sky:border-urban-dark happy-sand-sky:text-urban-dark;
    @apply happy-gray-sand:bg-border-gray-happy-light happy-gray-sand:border-urban-dark happy-gray-sand:text-urban-dark;
}

.form-option-title-product-happy {
    @apply flex flex-row items-center justify-between px-3 py-2 cursor-pointer;
}

.h4-option-happy {
    @apply flex font-semibold whitespace-pre-wrap;
}
.choice {
    @apply happy-light:text-border-red-happy-light;
    @apply happy-dark:text-border-red-happy-light;
    @apply happy-urban-dark-red:text-urban-dark;
    @apply happy-urban-light-red:text-bg-happy-light;
    @apply happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:text-bg-happy-light;
    @apply happy-fresh-mint:text-urban-red-dark;
    @apply happy-bee-light:text-urban-red;
    @apply happy-bee-dark:text-urban-red;
    @apply happy-earth-stone:text-urban-red-dark;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-urban-dark;
    @apply happy-gray-sand:text-urban-dark;
}

.svg-arrow-happy-option-product-happy {
    @apply min-w-5 min-h-5 max-w-5 max-h-5 transition-transform duration-300;
    @apply happy-light:fill-text-dark-happy-light;
    @apply happy-dark:fill-bg-happy-light;
    @apply happy-urban-dark-red:fill-urban-dark;
    @apply happy-urban-light-red:fill-bg-happy-light;
    @apply happy-orange-light:fill-urban-dark;
    @apply happy-orange-dark:fill-urban-dark;
    @apply happy-fresh-mint:fill-urban-dark;
    @apply happy-bee-light:fill-urban-dark;
    @apply happy-bee-dark:fill-bg-happy-light;
    @apply happy-earth-stone:fill-urban-dark;
    @apply happy-scorched-earth:fill-urban-dark;
    @apply happy-sand-sky:fill-urban-dark;
    @apply happy-gray-sand:fill-urban-dark;
}

.border-title-option-happy {
    @apply border-b;
    @apply happy-light:border-text-dark-happy-light;
    @apply happy-fresh-mint:border-urban-dark;
    @apply happy-bee-light:border-urban-dark;
    @apply happy-earth-stone:border-urban-dark;
    @apply happy-scorched-earth:border-urban-dark;
    @apply happy-sand-sky:border-urban-dark;
    @apply happy-gray-sand:border-urban-dark;
}

.option-selected-happy {
    @apply px-3 py-1 text-sm font-semibold border-t;
    @apply happy-light:border-text-dark-happy-light;

    @apply happy-fresh-mint:border-urban-dark;
    @apply happy-bee-light:border-urban-dark;
    @apply happy-earth-stone:border-urban-dark;
    @apply happy-scorched-earth:border-urban-dark;
    @apply happy-sand-sky:border-urban-dark;
    @apply happy-gray-sand:border-urban-dark;
}

.price-info-happy {
    @apply font-semibold;
    @apply happy-light:text-border-red-happy-light;
    @apply happy-dark:text-border-red-happy-light;
    @apply happy-urban-dark-red:text-urban-dark;
    @apply happy-urban-light-red:text-urban-dark;
    @apply happy-orange-light:text-bg-happy-light;
    @apply happy-orange-dark:text-bg-happy-light;
    @apply happy-fresh-mint:text-urban-red-dark;
    @apply happy-bee-light:text-urban-red;
    @apply happy-bee-dark:text-urban-red;
    @apply happy-earth-stone:text-urban-red-dark;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-red-eronor;
    @apply happy-gray-sand:text-red-eronor;
}
.dropdown-container {
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-origin: top;
}

.dropdown-closed {
    max-height: 0;
    opacity: 0;
}

.dropdown-open {
    max-height: 1200px;
    opacity: 1;
}

.fixed-bottom {
    @apply fixed rounded-none bottom-[56px] border-b w-full z-30 py-4 px-3 right-0;
    @apply flex items-end justify-between;
    @apply happy-light:text-bg-happy-light happy-light:border-bg-happy-light happy-light:bg-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light happy-dark:border-text-dark-happy-light happy-dark:bg-bg-happy-light;
    @apply happy-urban-dark-red:text-text-dark-happy-light happy-urban-dark-red:border-urban-dark happy-urban-dark-red:bg-bg-happy-light;
    @apply happy-urban-light-red:text-bg-happy-light happy-urban-light-red:border-bg-happy-light happy-urban-light-red:bg-urban-dark;
    @apply happy-orange-light:text-bg-happy-light happy-orange-light:border-bg-happy-light happy-orange-light:bg-urban-dark;
    @apply happy-orange-dark:text-text-dark-happy-light happy-orange-dark:border-bg-orange happy-orange-dark:bg-bg-happy-light;
    @apply happy-fresh-mint:text-bg-happy-light happy-fresh-mint:border-bg-happy-light happy-fresh-mint:bg-urban-dark;
    @apply happy-bee-light:text-bg-happy-light happy-bee-light:border-bg-happy-light happy-bee-light:bg-urban-dark;
    @apply happy-bee-dark:text-text-dark-happy-light happy-bee-dark:border-bee-yellow happy-bee-dark:bg-bg-happy-light;
    @apply happy-earth-stone:text-urban-dark happy-earth-stone:border-urban-dark happy-earth-stone:bg-earth-card;
    @apply happy-scorched-earth:text-bg-happy-light happy-scorched-earth:border-scorched-earth-yellow happy-scorched-earth:bg-scorched-earth-red;
    @apply happy-sand-sky:text-urban-dark happy-sand-sky:border-urban-dark happy-sand-sky:bg-sand-blue-dark;
    @apply happy-gray-sand:text-urban-dark happy-gray-sand:border-urban-dark happy-gray-sand:bg-gray-sand-gray-light;
}

.span-choice-happy-required {
    @apply font-semibold;
    @apply happy-light:text-border-red-happy-light;
    @apply happy-dark:text-border-red-happy-light;
    @apply happy-urban-dark-red:text-urban-red;
    @apply happy-urban-light-red:text-urban-red;
    @apply happy-orange-light:text-bg-happy-light;
    @apply happy-orange-dark:text-urban-dark;
    @apply happy-fresh-mint:text-bg-happy-light;
    @apply happy-bee-light:text-bg-happy-light;
    @apply happy-bee-dark:text-text-dark-happy-light;
    @apply happy-earth-stone:text-urban-red-dark;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-bg-happy-light;
    @apply happy-gray-sand:text-urban-dark;
}
.span-choice-happy {
    @apply text-button-yellow-happy-light font-semibold;
    @apply happy-light:text-button-yellow-happy-light;
    @apply happy-dark:text-button-yellow-happy-light;
    @apply happy-urban-dark-red:text-urban-red-dark;
    @apply happy-urban-light-red:text-urban-red-dark;
    @apply happy-orange-light:text-bg-orange;
    @apply happy-orange-dark:text-bg-orange;
    @apply happy-fresh-mint:text-bg-mint;
    @apply happy-bee-light:text-urban-red;
    @apply happy-bee-dark:text-urban-red;
    @apply happy-earth-stone:text-urban-red-dark;
    @apply happy-scorched-earth:text-scorched-earth-yellow;
    @apply happy-sand-sky:text-red-eronor;
    @apply happy-gray-sand:text-red-eronor;
}
.div-number-selecti-option-happy {
    @apply flex gap-2;
}

.modal-footer-product-happy {
    @apply w-full flex justify-between gap-2 py-2 px-3 z-30;
    @apply happy-dark:bg-bg-happy-light;
    @apply happy-light:bg-text-dark-happy-light;
    @apply happy-urban-dark-red:bg-bg-happy-light;
    @apply happy-urban-light-red:bg-urban-dark;
    @apply happy-orange-light:bg-urban-dark;
    @apply happy-orange-dark:bg-bg-happy-light;
    @apply happy-fresh-mint:bg-urban-dark;
    @apply happy-bee-light:bg-urban-dark;
    @apply happy-bee-dark:bg-bg-happy-light;
    @apply happy-earth-stone:bg-earth-card;
    @apply happy-scorched-earth:bg-scorched-earth-red;
    @apply happy-sand-sky:bg-sand-blue-dark;
    @apply happy-gray-sand:bg-gray-sand-gray-light;
}

.button-add-to-cart-happy {
    @apply min-w-36 py-2 px-2 rounded-lg font-semibold bg-button-yellow-happy-light;
    @apply shadow-sm;
    @apply happy-light:text-text-dark-happy-light happy-light:bg-button-yellow-happy-light happy-light:shadow-bg-gray-sand;
    @apply happy-dark:text-text-dark-happy-light happy-dark:bg-button-yellow-happy-light happy-dark:shadow-text-dark-happy-light;
    @apply happy-urban-dark-red:text-bg-happy-light happy-urban-dark-red:bg-urban-red happy-urban-dark-red:shadow-text-dark-happy-light;
    @apply happy-urban-light-red:text-bg-happy-light happy-urban-light-red:bg-urban-red happy-urban-light-red:shadow-bg-gray-sand;
    @apply happy-orange-light:text-urban-dark happy-orange-light:bg-bg-orange happy-orange-light:shadow-bg-gray-sand;
    @apply happy-orange-dark:text-urban-dark happy-orange-dark:bg-bg-orange happy-orange-dark:shadow-text-dark-happy-light;
    @apply happy-fresh-mint:text-bg-happy-light  happy-fresh-mint:bg-bg-mint happy-fresh-mint:shadow-bg-happy-light;
    @apply happy-bee-light:text-urban-dark happy-bee-light:bg-bee-yellow happy-bee-light:shadow-bg-happy-light;
    @apply happy-bee-dark:text-urban-dark happy-bee-dark:bg-bee-yellow happy-bee-dark:shadow-urban-dark;
    @apply happy-earth-stone:text-bg-happy-light happy-earth-stone:bg-urban-dark happy-earth-stone:shadow-urban-dark;
    @apply happy-scorched-earth:text-urban-dark happy-scorched-earth:bg-scorched-earth-yellow happy-scorched-earth:shadow-urban-dark;
    @apply happy-sand-sky:text-urban-dark happy-sand-sky:bg-bg-sand happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:text-urban-dark happy-gray-sand:bg-bg-gray-sand happy-gray-sand:shadow-urban-dark;
}
.button-add-to-cart-happy:active,
.button-add-to-cart-happy:focus {
    @apply happy-light:bg-hover-yellow-happy;
    @apply happy-dark:bg-hover-yellow-happy;
    @apply happy-urban-dark-red:bg-urban-red-dark;
    @apply happy-urban-light-red:bg-urban-red-dark;
    @apply happy-orange-light:bg-bg-orange-dark happy-orange-light:text-bg-happy-light;
    @apply happy-orange-dark:bg-bg-orange-dark happy-orange-dark:text-bg-happy-light;
    @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:text-bg-happy-light;
    @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:text-bg-happy-light;
    @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:text-bg-happy-light;
    @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:text-bg-happy-light;
    @apply happy-scorched-earth:bg-bg-scorched-earth happy-scorched-earth:text-urban-dark;
    @apply happy-sand-sky:bg-sand-blue happy-sand-sky:text-urban-dark;
    @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:text-urban-dark;
}

.section-product-associated-happy {
    @apply flex flex-col overflow-y-auto pt-14 px-1;

    min-height: fit-content;
}

@media (hover: hover) {
    .div-button-close-product-selected-happy:hover {
        @apply happy-light:bg-border-gray-happy-light;
        @apply happy-dark:bg-border-gray-happy-light;
        @apply happy-urban-dark-red:bg-urban-red-dark;
        @apply happy-urban-light-red:bg-urban-red-dark;
        @apply happy-orange-light:bg-bg-orange-dark;
        @apply happy-orange-dark:bg-bg-orange-dark;
        @apply happy-fresh-mint:bg-bg-mint-dark;
        @apply happy-bee-light:bg-bee-yellow-dark;
        @apply happy-bee-dark:bg-bee-yellow-dark;
        @apply happy-earth-stone:bg-earth-card-dark;
        @apply happy-scorched-earth:bg-scorched-earth-red;
        @apply happy-sand-sky:bg-sand-blue-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-light;
    }
    .div-button-close-product-selected-happy:hover .svg-cross-product-selected-happy {
        @apply happy-light:stroke-text-dark-happy-light;
        @apply happy-dark:stroke-text-dark-happy-light;
        @apply happy-urban-dark-red:stroke-bg-happy-light;
        @apply happy-urban-light-red:stroke-bg-happy-light;
        @apply happy-orange-light:stroke-bg-happy-light;
        @apply happy-orange-dark:stroke-bg-happy-light;
        @apply happy-fresh-mint:stroke-bg-happy-light;
        @apply happy-bee-light:stroke-bg-happy-light;
        @apply happy-bee-dark:stroke-bg-happy-light;
        @apply happy-earth-stone:stroke-bg-happy-light;
        @apply happy-scorched-earth:stroke-bg-happy-light;
        @apply happy-sand-sky:stroke-bg-happy-light;
        @apply happy-gray-sand:stroke-urban-dark;
    }

    .button-add-to-cart-happy:hover {
        @apply happy-light:bg-hover-yellow-happy;
        @apply happy-dark:bg-hover-yellow-happy;
        @apply happy-urban-dark-red:bg-urban-red-dark;
        @apply happy-urban-light-red:bg-urban-red-dark;
        @apply happy-orange-light:bg-bg-orange-dark happy-orange-light:text-bg-happy-light;
        @apply happy-orange-dark:bg-bg-orange-dark happy-orange-dark:text-bg-happy-light;
        @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:text-bg-happy-light;
        @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:text-bg-happy-light;
        @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:text-bg-happy-light;
        @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:text-bg-happy-light;
        @apply happy-scorched-earth:bg-bg-scorched-earth happy-scorched-earth:text-urban-dark;
        @apply happy-sand-sky:bg-sand-blue happy-sand-sky:text-urban-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:text-urban-dark;
    }
}

@media screen and (min-width: 416px) {
    .section-selected-product-happy {
        @apply max-w-[26rem];
    }
    .div-option-selected-happy {
        @apply max-w-[26rem];
    }
    .modal-footer-product-happy {
        @apply max-w-[26rem];
    }
}

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(5deg);
    }
    30% {
        transform: rotate(0eg);
    }
    45% {
        transform: rotate(-5deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-5deg);
    }
    100% {
        transform: rotate(0deg);
    }
}

.shake {
    animation: shake 0.5s ease-in-out;
}
</style>
